import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Layout,
  Typography,
  Form,
  Input,
  Button,
  notification,
  Modal,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import './Login.css';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setModalMessage('Invalid reset link.');
      setIsModalVisible(true);
    }
  }, [token]);

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch('/backend/reset_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, token }),
      });
      const data = await response.json();

      if (response.ok) {
        notification.success({
          message: 'Password Reset',
          description: 'Your password has been updated. Log in now.',
          duration: 5,
        });
        form.resetFields();
        navigate('/login');
      } else {
        setModalMessage(data.error || 'An error occurred.');
        setIsModalVisible(true);
      }
    } catch (error) {
      setModalMessage('Network error. Please check your connection.');
      setIsModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = () => {
    notification.error({
      message: 'Validation Failed',
      description: 'Please check your passwords.',
      duration: 3,
    });
  };

  const validatePassword = (_, value) => {
    if (!value) return Promise.reject(new Error('Password required!'));
    if (value.length < 6 || !/[A-Z]/.test(value) || !/[0-9]/.test(value)) {
      return Promise.reject(new Error('Password must be at least 6 characters with an uppercase letter and a number.'));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('Passwords do not match!'));
    },
  });

  const handleModalOk = () => {
    setIsModalVisible(false);
    setModalMessage('');
    if (!token) navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          background: '#ffffff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 15px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
        aria-label="NH Guide Header"
      >
        <Title level={4} style={{ margin: 0, color: '#4a90e2', fontWeight: 700, fontSize: 20 }}>
          NH Guide
        </Title>
      </Header>

      <Content style={{ flex: 1, padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="login-form-container">
          <Title
            level={3}
            style={{ textAlign: 'center', color: 'rgb(44, 62, 80)', fontWeight: 800, marginBottom: 15, fontSize: 28 }}
            aria-label="Reset Password Title"
          >
            New Password
          </Title>
          <Text
            style={{ textAlign: 'center', color: 'rgb(127, 140, 141)', marginBottom: 25, display: 'block', fontSize: 16, fontStyle: 'italic' }}
            aria-label="Instruction Message"
          >
            Enter your new password.
          </Text>
          <Form
            form={form}
            name="reset-password"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            style={{ maxWidth: '500px', width: '100%' }}
            validateTrigger="onBlur"
          >
            <Form.Item
              label="Password"
              name="password"
              rules={[{ validator: validatePassword }]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="New password"
                size="large"
                className="custom-input"
                aria-label="Password Field"
              />
            </Form.Item>

            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              rules={[validateConfirmPassword({ getFieldValue: form.getFieldValue })]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="Confirm password"
                size="large"
                className="custom-input"
                aria-label="Confirm Password Field"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: '100%', background: 'linear-gradient(to right, rgb(44, 62, 80), rgb(52, 73, 94))', borderColor: 'rgb(44, 62, 80)' }}
                loading={isLoading}
                aria-label="Reset Password Button"
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: 'center',
          padding: '15px 0',
          background: 'rgb(255, 255, 255)',
          boxShadow: '0 -2px 15px rgba(0, 0, 0, 0.05)',
          color: 'rgb(127, 140, 141)',
          fontSize: 12,
        }}
        aria-label="Footer"
      >
        <Text style={{ display: 'block', marginTop: 5, fontSize: 10 }}>
          © 2025 NH Guide. All rights reserved.
        </Text>
      </Footer>

      <Modal
        title="Error"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={handleModalOk}
            style={{ background: 'rgb(74, 144, 226)', border: 'none', borderRadius: '8px', padding: '0 20px' }}
            aria-label="Close Modal"
          >
            OK
          </Button>,
        ]}
        style={{ top: '20%', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)' }}
        bodyStyle={{ textAlign: 'center', fontSize: '16px', color: 'rgb(44, 62, 80)', padding: '25px' }}
        aria-label="Error Modal"
      >
        <p>{modalMessage}</p>
      </Modal>
    </Layout>
  );
};

export default ResetPassword;